/* eslint-disable camelcase */
import type { ReactNode } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import styles from './PrereadFeedback.module.scss'

import type { EvidenceMetadata, EvidenceStats } from '@/api/prereadFeedback'
import DataTable from '@/components/DataTable'

export const sourceLabel = (source: string) =>
  ({
    gmail: 'Email (Gmail)',
    slack: 'Slack',
    prior_meetings: 'Prior meetings',
    notion: 'Notion',
    attendee_details: 'Attendee details',
    web: 'Web',
    wispr: 'Wispr',
    calendar: 'Calendar',
    unknown: 'Unknown',
  })[source] ?? source
const humanize = (value: string) => value.replace(/_/g, ' ')
const number = (value: number | null) => (value === null ? 'Unknown' : value.toLocaleString())
const measured = (value: number | null, count: number) => (
  <span>
    {number(value)}
    <small className={styles.cellNote}>{count.toLocaleString()} measured briefs</small>
  </span>
)
const fixed = { enableSorting: false }

const prefetchColumns: ColumnDef<EvidenceStats['prefetch'][number]>[] = [
  {
    ...fixed,
    header: 'Source / stage',
    accessorKey: 'source',
    cell: ({ row }) => (
      <>
        <strong>{sourceLabel(row.original.source)}</strong>
        <small className={styles.cellNote}>{humanize(row.original.retrieval)}</small>
      </>
    ),
  },
  { ...fixed, header: 'Briefs observed', accessorKey: 'observedGenerations' },
  {
    ...fixed,
    header: 'Retrieved',
    cell: ({ row }) => measured(row.original.retrievedCount, row.original.retrievedMeasuredGenerations),
  },
  {
    ...fixed,
    header: 'Retained',
    cell: ({ row }) => measured(row.original.retainedCount, row.original.retainedMeasuredGenerations),
  },
  {
    ...fixed,
    header: 'Recent retained',
    cell: ({ row }) => measured(row.original.recentCount, row.original.recentMeasuredGenerations),
  },
  {
    ...fixed,
    header: 'Outcomes',
    cell: ({ row }) =>
      Object.entries(row.original.statusCounts)
        .map(([key, value]) => `${humanize(key)}: ${value}`)
        .join(' · '),
  },
]
const researchColumns: ColumnDef<EvidenceStats['research'][number]>[] = [
  { ...fixed, header: 'Tool', accessorKey: 'source', cell: ({ row }) => sourceLabel(row.original.source) },
  { ...fixed, header: 'Operation', accessorKey: 'operation' },
  { ...fixed, header: 'Observed calls', accessorKey: 'calls' },
  { ...fixed, header: 'Failed calls', accessorKey: 'failedCalls' },
  {
    ...fixed,
    header: 'Returned items',
    cell: ({ row }) => (
      <span>
        {number(row.original.returnedItemCount)}
        <small className={styles.cellNote}>{row.original.returnedMeasuredCalls.toLocaleString()} measured calls</small>
      </span>
    ),
  },
]

export const AttemptsPerMeeting = ({ rows }: { rows: EvidenceStats['attemptsPerMeeting'] }) => {
  const data = rows.map((row, index) => ({
    attempts: index === rows.length - 1 ? row.attempts + '+' : String(row.attempts),
    meetings: row.meetings,
  }))
  return (
    <section className={styles.panel} aria-label="Generations per meeting">
      <h2>Generations per meeting</h2>
      <p className={styles.caption}>Unique meetings by how many generation attempts they had in this range.</p>
      {rows.some((row) => row.meetings > 0) ? (
        <div className={styles.attemptsChart}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data} margin={{ top: 12, right: 24, bottom: 0, left: 0 }} accessibilityLayer>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="attempts" />
              <YAxis allowDecimals={false} width={56} />
              <Tooltip labelFormatter={(label) => label + ' generations'} />
              <Bar dataKey="meetings" name="Meetings" fill="var(--attempts-bar-color)" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className={styles.caption}>No generations in this range.</p>
      )}
    </section>
  )
}

export const OverallEvidence = ({ stats, children }: { stats: EvidenceStats; children?: ReactNode }) => (
  <>
    {stats.diagnosticsTruncated && (
      <p className={styles.notice} role="status">
        Evidence diagnostics consider at most the newest {stats.diagnosticsLimit.toLocaleString()} successful briefs.
        This period exceeds that limit. Narrow the dates for full coverage.
      </p>
    )}
    <div className={styles.metrics}>
      <Metric label="Generation attempts" value={stats.totalGenerations} />
      <Metric label="Successful briefs" value={stats.successfulGenerations} />
      <Metric label="Empty briefs" value={stats.emptyGenerations} />
      <Metric label="Failed briefs" value={stats.failedGenerations} />
      <Metric label="Briefs with evidence" value={stats.observedGenerations} />
      <Metric label="Evidence unavailable" value={stats.unobservedGenerations} />
    </div>
    {children}
    <p className={styles.caption}>
      Evidence coverage: {stats.observedGenerations.toLocaleString()} of {stats.sampledGenerations.toLocaleString()}{' '}
      inspected successful briefs. Missing or unsupported metadata stays unknown.
    </p>
    <section className={styles.panel}>
      <h2>Evidence supplied to the writer</h2>
      <p className={styles.caption}>
        Provider and filtered stages can overlap. Counts describe retrieved and retained items, not citations or
        usefulness. Recent means within 14 days of generation.
      </p>
      <DataTable
        data={stats.prefetch}
        columns={prefetchColumns}
        ariaLabel="Overall prefetch evidence"
        emptyMessage="No prefetch observations in this population."
      />
    </section>
    <section className={styles.panel}>
      <h2>Writer tool usage</h2>
      <p className={styles.caption}>
        Research was observed for {stats.researchObservedGenerations.toLocaleString()} briefs and complete for{' '}
        {stats.researchCompleteGenerations.toLocaleString()}. Incomplete observations are lower bounds; unobserved tools
        are unknown.
      </p>
      <DataTable
        data={stats.research}
        columns={researchColumns}
        ariaLabel="Overall writer tool usage"
        emptyMessage="No writer tool calls observed. Unobserved research does not mean zero calls."
      />
    </section>
  </>
)

export const Metric = ({ label, value }: { label: string; value: number | string }) => (
  <div className={styles.metric}>
    <span>{label}</span>
    <strong>{value.toLocaleString()}</strong>
  </div>
)

type DetailPrefetch = { source: string; retrieval: string } & EvidenceMetadata['prefetch'][string][string]
type DetailResearch = NonNullable<EvidenceMetadata['writer_research']>['calls'][number]
const detailPrefetchColumns: ColumnDef<DetailPrefetch>[] = [
  {
    ...fixed,
    header: 'Source / stage',
    cell: ({ row }) => (
      <>
        {sourceLabel(row.original.source)}
        <small className={styles.cellNote}>{humanize(row.original.retrieval)}</small>
      </>
    ),
  },
  { ...fixed, header: 'Status', cell: ({ row }) => humanize(row.original.status) },
  { ...fixed, header: 'Retrieved', cell: ({ row }) => number(row.original.retrieved_count) },
  { ...fixed, header: 'Retained', cell: ({ row }) => number(row.original.relevant_count) },
  { ...fixed, header: 'Recent retained', cell: ({ row }) => number(row.original.recent_relevant_count) },
  {
    ...fixed,
    header: 'Newest evidence',
    cell: ({ row }) =>
      row.original.newest_evidence_age_seconds === null
        ? 'Unknown'
        : `${number(row.original.newest_evidence_age_seconds)} seconds old`,
  },
  { ...fixed, header: 'Selection', cell: ({ row }) => humanize(row.original.selection_status) },
  {
    ...fixed,
    header: 'Truncated',
    cell: ({ row }) => (row.original.truncated === null ? 'Unknown' : row.original.truncated ? 'Yes' : 'No'),
  },
]
const detailResearchColumns: ColumnDef<DetailResearch>[] = [
  { ...fixed, header: 'Tool', cell: ({ row }) => sourceLabel(row.original.source) },
  { ...fixed, header: 'Operation', accessorKey: 'operation' },
  { ...fixed, header: 'Status', cell: ({ row }) => humanize(row.original.status) },
  { ...fixed, header: 'Returned items', cell: ({ row }) => number(row.original.returned_item_count) },
  {
    ...fixed,
    header: 'Error',
    cell: ({ row }) => (row.original.error_category ? humanize(row.original.error_category) : '—'),
  },
  {
    ...fixed,
    header: 'Truncated',
    cell: ({ row }) => (row.original.truncated === null ? 'Unknown' : row.original.truncated ? 'Yes' : 'No'),
  },
]

export const BriefEvidence = ({ evidence }: { evidence: EvidenceMetadata | null }) => {
  if (!evidence)
    return (
      <p className={styles.notice}>
        Evidence is unavailable for this report. Older briefs and reports without an exact generation match may have no
        diagnostics.
      </p>
    )
  const rows = Object.entries(evidence.prefetch).flatMap(([source, entries]) =>
    Object.entries(entries).map(([retrieval, entry]) => ({ source, retrieval, ...entry }))
  )
  return (
    <>
      <h3>Prefetch evidence</h3>
      <p className={styles.caption}>
        Counts describe evidence supplied to the writer. Recent means within {evidence.recent_window_days} days of
        generation; unknown values were not measured.
      </p>
      <DataTable
        data={rows}
        columns={detailPrefetchColumns}
        ariaLabel="Brief prefetch evidence"
        emptyMessage="No prefetch observations recorded."
      />
      <h3>Writer tool calls</h3>
      <p className={styles.caption}>
        {evidence.writer_research === null
          ? 'Writer research was not observed; tool usage is unknown.'
          : evidence.writer_research.complete
            ? 'Writer research observations are complete.'
            : 'Writer research observations are incomplete; calls shown are a lower bound.'}
      </p>
      {evidence.writer_research && (
        <DataTable
          data={evidence.writer_research.calls}
          columns={detailResearchColumns}
          ariaLabel="Brief writer tool calls"
          emptyMessage="No tool calls recorded in the observed research."
        />
      )}
      <details className={styles.raw}>
        <summary>Evidence JSON (version {evidence.version})</summary>
        <pre>{JSON.stringify(evidence, null, 2)}</pre>
      </details>
    </>
  )
}
